import { useState, useEffect, useMemo } from 'react';
import { Box, Button, FormControl, InputLabel, Select, MenuItem, TextField, Typography, Divider, Paper, IconButton, CircularProgress, InputAdornment, FormControlLabel, Switch, Container } from '@mui/material';
import { AddCircle, RemoveCircle, Save, Public, LocalOffer } from '@mui/icons-material';
import { useAuth } from '../../hooks/useAuth';
import { ALL_COUNTRIES } from '../../helperFunc/countylist';
import { getPaymentSettings, setPaymentSettings } from '../../hooks/apiHandler';



export const PaymentSettingPage = ({ storeInfo }) => {
    const { Logout } = useAuth();
    const [, setConfig] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    // Initialize country/region from props if available, otherwise mock data
    const [selectedCountry, ] = useState(storeInfo.country);
    const [selectedRegion, ] = useState(storeInfo.state);

    const [activeTaxRates, setActiveTaxRates] = useState([]);
    const [discounts, setDiscounts] = useState({
        enableDiscount: false,
        discountPer: [],
        discountAmount: [],
    });
    const [tipSettings, setTipSettings] = useState({
        enableTipping: false,
        tipPer: [5, 10, 15, 20],
        tipAmount: [1.00, 2.00, 5.00, 10]
    });
    const [terminals, setTerminals] = useState([]);

    const availableRegions = useMemo(() => {
        return ALL_COUNTRIES.find(c => c.code === selectedCountry)?.regions || [];
    }, [selectedCountry]);

    // --- Data Fetching ---
    useEffect(() => {
        async function fetchConfig() {
            try {
                // console.log(storeInfo);
                // Fetch Store-specific payment configuration
                const response = await getPaymentSettings({ storeID: storeInfo.storeID });
                const data = response.data;
                // console.log(data);
                if (data) {
                    setActiveTaxRates(data.taxRates || []);
                    setDiscounts(data.discountOption || {
                        enableDiscount: false,
                        discountPer: [],
                        discountAmount: [],
                    });
                    setTipSettings(data.tipOptions || { enableTipping: false, defaultPercentage: [15, 18, 20], defaultAmount: [1.00, 2.00, 5.00] });
                    setTerminals(data.terminals || []);
                    setConfig(data);
                } else {
                    console.log("Let Go with Default...");
                }


            } catch (e) {
                console.error("Failed to load payment config:", e);
                setActiveTaxRates([]);
                setDiscounts([]);
            } finally {
                setIsLoading(false);
            }
        }
        fetchConfig();
    }, [storeInfo, Logout]);


    // --- Tax Handlers ---
    const handleTaxRateChange = (index, field, value) => {
        const newRates = [...activeTaxRates];
        let newValue = value;

        if (field === 'rate' && value !== '') {
            newValue = parseFloat(value);
        } else if (field === 'isRecoverable') {
            newValue = value; // Boolean from switch
        }

        newRates[index] = {
            ...newRates[index],
            [field]: newValue
        };
        setActiveTaxRates(newRates);
    };

    const handleRemoveTaxRate = (index) => setActiveTaxRates(prev => prev.filter((_, i) => i !== index));
    const handleAddTaxRate = () => setActiveTaxRates(prev => [...prev, { name: 'New Tax', rate: 1.00, isRecoverable: false, reg: '' }]);

    // --- Discount Handlers ---
    // --- Discount Handlers ---
    // Handler for changes within discount item arrays (discountPer or discountAmount)
    const handleDiscountItemChange = (arrayName, index, field, value) => {
        setDiscounts(prevDiscounts => {
            const newArray = [...prevDiscounts[arrayName]];
            let newValue = value;

            if (field === 'rate') {
                newValue = parseFloat(value) || 0;
            } else if (field === 'enabled') {
                newValue = value;
            }

            newArray[index] = { ...newArray[index], [field]: newValue };

            return { ...prevDiscounts, [arrayName]: newArray };
        });
    };

    // Handler for adding new discount items
    const handleAddDiscount = (arrayName) => {
        setDiscounts(prevDiscounts => {
            const currentArray = prevDiscounts[arrayName];
            // Check overall slot limit (4 total slots across both arrays)
            if (prevDiscounts.discountPer.length + prevDiscounts.discountAmount.length >= 4) {
                alert("Maximum of 4 static discount slots allowed across all types.");
                return prevDiscounts;
            }

            const newDiscount = {
                name: `New ${arrayName === 'discountPer' ? 'Percent' : 'Amount'}`,
                rate: 0,
                enabled: false
            };

            return {
                ...prevDiscounts,
                [arrayName]: [...currentArray, newDiscount]
            };
        });
    }

    // Handler for removing discount items
    const handleRemoveDiscount = (arrayName, index) => {
        setDiscounts(prevDiscounts => {
            const newArray = prevDiscounts[arrayName].filter((_, i) => i !== index);
            return { ...prevDiscounts, [arrayName]: newArray };
        });
    };


    // // --- Terminal Handlers ---
    // const handleTerminalPaymentToggle = (terminalId, paymentType, isChecked) => {
    //     setTerminals(prev => prev.map(term =>
    //         term.id === terminalId ? {
    //             ...term,
    //             enabledPayments: { ...term.enabledPayments, [paymentType]: isChecked }
    //         } : term
    //     ));
    // };

    // // --- Tip Input Handler (CSV/Array conversion) ---
    // const handleTipAmountChange = (type, value) => {
    //     const amounts = value.split(',').map(n => parseFloat(n.trim()) || 0).filter(n => n >= 0);
    //     setTipSettings(p => ({
    //         ...p,
    //         [type]: amounts
    //     }));
    // };

    // --- Save Logic ---
    const handleSave = async () => {
        // console.log(storeInfo.storeID);
        const payload = {
            storeID: storeInfo.storeID,
            taxRates: activeTaxRates,
            discountOption: discounts, // Filter out empty discounts
            tipOptions: tipSettings,
            terminals: terminals,
            currency: ALL_COUNTRIES.find(c => c.code === selectedCountry)?.currency || 'USD'
        };
        // console.log(payload);
        // Call the API to save the new configuration
        const response = await setPaymentSettings(payload);
        // console.log(response);
        if (response) {
            alert("Payment configuration saved successfully!");
            setConfig(prev => ({ ...prev, ...payload }));
        } else {
            alert("Error saving configuration.");
        }
    };

    if (isLoading) return <CircularProgress />;
    return (
        <Container maxWidth="xl" sx={{ py: 4 }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
                Payment & Terminal Settings
            </Typography>

            <Button variant="contained" onClick={handleSave} sx={{ mb: 4 }} startIcon={<Save />}>
                Save All Configurations
            </Button>


            {/* SECTION 1: TAX & GEO CONFIGURATION */}
            <Paper elevation={3} sx={{ p: 3, mb: 4, backgroundColor: '#f0f4f8' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Public color="primary" sx={{ mr: 1 }} />
                    <Typography variant="h6" color="primary.dark">Tax Configuration</Typography>
                </Box>

                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
                    {/* Country Selector (Read-only as per General Settings) */}
                    <FormControl sx={{ minWidth: 150 }} size="small">
                        <InputLabel>Country</InputLabel>
                        <Select value={selectedCountry} label="Country" disabled>
                            {ALL_COUNTRIES.map(c => <MenuItem key={c.code} value={c.code}>{c.name}</MenuItem>)}
                        </Select>
                    </FormControl>

                    {/* State/Region Selector (Read-only as per General Settings) */}
                    <FormControl sx={{ minWidth: 150 }} size="small" disabled={availableRegions.length === 0}>
                        <InputLabel>State / Province</InputLabel>
                        <Select value={selectedRegion} label="State / Province" disabled>
                            {availableRegions.map(r => <MenuItem key={r.code} value={r.code}>{r.name}</MenuItem>)}
                        </Select>
                    </FormControl>
                </Box>

                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>Active Tax Components</Typography>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {activeTaxRates.map((tax, index) => (
                        <Box key={index} sx={{ display: 'flex', gap: 1, alignItems: 'center', backgroundColor: index % 2 === 0 ? '#fafafa' : '#fff', p: 1, borderRadius: 1 }}>

                            {/* Tax Name Input */}
                            <TextField label="Tax Name (e.g., GST)" size="small" value={tax.name} onChange={(e) => handleTaxRateChange(index, 'name', e.target.value)} required sx={{ minWidth: 30, flexGrow: 2 }} />

                            {/* Tax Registration Number */}
                            <TextField label="Tax Reg No:" size="small" value={tax.reg} onChange={(e) => handleTaxRateChange(index, 'reg', e.target.value)} required sx={{ minWidth: 100, flexGrow: 4 }} />

                            {/* Rate Input */}
                            <TextField label="Rate (%)" size="small" type="number" value={tax.rate} onChange={(e) => handleTaxRateChange(index, 'rate', e.target.value)} required sx={{ minWidth: 100, flexGrow: 1 }}
                                InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment>, inputProps: { step: 0.01, min: 1 } }}
                            />

                            {/* Recoverable Switch
                            <FormControlLabel
                                control={<Switch checked={tax.isRecoverable || false} onChange={(e) => handleTaxRateChange(index, 'isRecoverable', e.target.checked)} name="isRecoverable" />}
                                label="Recoverable"
                                labelPlacement="start"
                            /> */}

                            <IconButton color="error" onClick={() => handleRemoveTaxRate(index)} disabled={activeTaxRates.length === 1}>
                                <RemoveCircle />
                            </IconButton>
                        </Box>
                    ))}

                    <Button variant="outlined" startIcon={<AddCircle />} onClick={handleAddTaxRate} sx={{ mt: 1 }}>
                        Add Tax Component
                    </Button>
                </Box>
            </Paper>

            {/* SECTION 2: DISCOUNT & TIP CONFIGURATION */}
            <Paper elevation={3} sx={{ p: 3, mb: 4, backgroundColor: '#f0f4f8' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <LocalOffer color="secondary" sx={{ mr: 1 }} />
                    <Typography variant="h6" color="secondary.dark">Discount & Tip Configuration</Typography>
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* Tip Configuration */}
                <Box sx={{ mb: 3 }}>
                    {/* Static Discount Configuration */}
                    <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>Discounts Options</Typography>
                    <FormControlLabel
                        control={<Switch checked={discounts.enableDiscount} onChange={(e) => setDiscounts(p => ({ ...p, enableDiscount: e.target.checked }))} />}
                        label="Enable Discounts"
                    />
                    {discounts.enableDiscount && (
                        <Box key={'dis'} sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 2, p: 1, border: '1px dashed #ccc', borderRadius: 1 }}>
                                {/* Percentage Inputs */}
                                <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>Discount by Percentage (%)</Typography>
                                {(discounts.discountPer) ? discounts.discountPer.map((d, index) => (
                                    <Box key={'p-' + index} sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1 }}>
                                        <Switch checked={d.enabled} onChange={(e) => handleDiscountItemChange('discountPer', index, 'enabled', e.target.checked)} size="small" disabled={!discounts.enableDiscount} />
                                        <TextField label="Name" size="small" value={d.name} onChange={(e) => handleDiscountItemChange('discountPer', index, 'name', e.target.value)} sx={{ flexGrow: 4 }} disabled={!discounts.enableDiscount} />
                                        <TextField label="Rate (%)" size="small" type="number" value={d.rate} onChange={(e) => handleDiscountItemChange('discountPer', index, 'rate', e.target.value)}
                                            InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment>, inputProps: { min: 0, max: 100 } }} sx={{ width: 80, flexGrow: 1 }} disabled={!discounts.enableDiscount}
                                        />
                                        <IconButton color="error" onClick={() => handleRemoveDiscount('discountPer', index)} disabled={discounts.discountPer?.length <= 1}><RemoveCircle /></IconButton>
                                    </Box>
                                )) : ""}
                                <Button variant="text" onClick={() => handleAddDiscount('discountPer')} startIcon={<AddCircle />} sx={{ textTransform: 'none', mt: 1 }} disabled={discounts.discountPer?.length >= 4}>
                                    Add Discount Slot ({discounts.discountPer?.length}/4)
                                </Button>
                                <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>Discount by Amount (%)</Typography>
                                {(discounts.discountAmount) ? discounts.discountAmount.map((d, index) => (
                                    <Box key={'a-' + index} sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1 }}>
                                        <Switch checked={d.enabled} onChange={(e) => handleDiscountItemChange('discountAmount', index, 'enabled', e.target.checked)} size="small" disabled={!discounts.enableDiscount} />
                                        <TextField label="Name" size="small" value={d.name} onChange={(e) => handleDiscountItemChange('discountAmount', index, 'name', e.target.value)} sx={{ flexGrow: 4 }} disabled={!discounts.enableDiscount} />
                                        <TextField label="Amount ($)" size="small" type="number" value={d.rate} onChange={(e) => handleDiscountItemChange('discountAmount', index, 'rate', e.target.value)}
                                            InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment>, inputProps: { min: 0 } }} sx={{ width: 80, flexGrow: 1 }} disabled={!discounts.enableDiscount}
                                        />
                                        <IconButton color="error" onClick={() => handleRemoveDiscount('discountAmount', index)} disabled={discounts.discountAmount.length <= 1}><RemoveCircle /></IconButton>
                                    </Box>
                                )) : ""}
                                <Button variant="text" onClick={() => handleAddDiscount('discountAmount')} startIcon={<AddCircle />} sx={{ textTransform: 'none', mt: 1 }} disabled={((discounts.discountAmount) ? discounts.discountAmount.length : 0) >= 4}>
                                    Add Discount Slot ({discounts.discountAmount.length}/4)
                                </Button>
                            </Box>
                        </Box>)}

                    <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 3, mb: 1 }}>Tip Options</Typography>
                    <FormControlLabel
                        control={<Switch checked={tipSettings.enableTipping} onChange={(e) => setTipSettings(p => ({ ...p, enableTipping: e.target.checked }))} />}
                        label="Enable Customer Tipping"
                    />

                    {tipSettings.enableTipping && (
                        <></>
                        // <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 2, p: 1, border: '1px dashed #ccc', borderRadius: 1 }}>
                        //     {/* Percentage Inputs */}
                        //     <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>Tip by Percentage (%)</Typography>
                        //     <Box key={'perc-opt-box'} sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1 }}>
                        //         <TextField
                        //             label="Percentage Options (CSV)"
                        //             size="small"
                        //             value={''}
                        //             onChange={(e) => handleTipAmountChange('tipPer', e.target.value)}

                        //             sx={{ minWidth: 50, flexGrow: 1 }}
                        //         />
                        //         <TextField
                        //             label="Percentage Options (CSV)"
                        //             size="small"
                        //             value={''}
                        //             onChange={(e) => handleTipAmountChange('defaultPercentage', e.target.value)}

                        //             sx={{ minWidth: 50, flexGrow: 1 }}
                        //         />
                        //         <TextField
                        //             label="Percentage Options (CSV)"
                        //             size="small"
                        //             value={''}
                        //             onChange={(e) => handleTipAmountChange('defaultPercentage', e.target.value)}

                        //             sx={{ minWidth: 50, flexGrow: 1 }}
                        //         />
                        //         <TextField
                        //             label="Percentage Options (CSV)"
                        //             size="small"
                        //             value={''}
                        //             onChange={(e) => handleTipAmountChange('defaultPercentage', e.target.value)}
                        //             sx={{ minWidth: 50, flexGrow: 1 }}
                        //         />

                        //     </Box>

                        //     {/* Amount Inputs */}
                        //     <Typography variant="subtitle2" fontWeight="bold" sx={{ mt: 2, mb: 1 }}>Tip by Fixed Amount ($)</Typography>
                        //     <Box key={'amount-opt-box'} sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1 }}>
                        //         <TextField
                        //             label="Amount Options (CSV)"
                        //             size="small"
                        //             value={''}
                        //             onChange={(e) => handleTipAmountChange('defaultAmount', e.target.value)}
                        //             helperText="Enter 3 or 4 fixed amounts, e.g., 1.00, 2.00, 5.00"
                        //             sx={{ minWidth: 50, flexGrow: 1 }}
                        //         />
                        //         <TextField
                        //             label="Amount Options (CSV)"
                        //             size="small"
                        //             value={tipSettings.defaultAmount.join(', ')}
                        //             onChange={(e) => handleTipAmountChange('defaultAmount', e.target.value)}
                        //             helperText="Enter 3 or 4 fixed amounts, e.g., 1.00, 2.00, 5.00"
                        //             sx={{ minWidth: 50, flexGrow: 1 }}
                        //         />
                        //         <TextField
                        //             label="Amount Options (CSV)"
                        //             size="small"
                        //             value={tipSettings.defaultAmount.join(', ')}
                        //             onChange={(e) => handleTipAmountChange('defaultAmount', e.target.value)}
                        //             helperText="Enter 3 or 4 fixed amounts, e.g., 1.00, 2.00, 5.00"
                        //             sx={{ minWidth: 50, flexGrow: 1 }}
                        //         />
                        //         <TextField
                        //             label="Amount Options (CSV)"
                        //             size="small"
                        //             value={tipSettings.defaultAmount.join(', ')}
                        //             onChange={(e) => handleTipAmountChange('defaultAmount', e.target.value)}
                        //             helperText="Enter 3 or 4 fixed amounts, e.g., 1.00, 2.00, 5.00"
                        //             sx={{ minWidth: 50, flexGrow: 1 }}
                        //         />

                        //     </Box>
                        // </Box>
                    )}
                </Box>

            </Paper>

            {/* <Paper elevation={3} sx={{ p: 3, height: '100%', backgroundColor: '#e3f2fd' }}>
                <Box sx={{ position: 'relative', width: '100%', height: '100%', background: '#ffffffe0', left: 0, top: 0, display: "flex", justifyContent: 'center', alignItems: 'center' }} >
                    <Typography sx={{ fontSize: '34px' }}> Coming Soon</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <PointOfSale color="info" sx={{ mr: 1 }} />
                    <Typography variant="h6" color="info.dark">Terminal Payment Toggles</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Control which payment methods are active on each POS terminal.
                </Typography>

                {terminals.map((terminal) => (
                    <Box key={terminal.id} sx={{ mb: 3, p: 2, border: '1px solid #bbdefb', borderRadius: 1 }}>
                        <Typography variant="subtitle1" fontWeight="bold" color="primary">{terminal.name} ({terminal.id})</Typography>
                        <Divider sx={{ my: 1 }} />

                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mt: 1 }}>

                            
                            <FormControlLabel
                                control={<Switch checked={terminal.enabledPayments.Cash} onChange={(e) => handleTerminalPaymentToggle(terminal.id, 'Cash', e.target.checked)} />}
                                label="Cash"
                            />
                            
                            <FormControlLabel
                                control={<Switch checked={terminal.enabledPayments.Card} onChange={(e) => handleTerminalPaymentToggle(terminal.id, 'Card', e.target.checked)} />}
                                label="Card"
                            />
                            
                            <FormControlLabel
                                control={<Switch checked={terminal.enabledPayments.GiftCard} onChange={(e) => handleTerminalPaymentToggle(terminal.id, 'GiftCard', e.target.checked)} />}
                                label="Gift Card"
                            />
                            
                            <FormControlLabel
                                control={<Switch checked={terminal.enabledPayments.Online} onChange={(e) => handleTerminalPaymentToggle(terminal.id, 'Online', e.target.checked)} />}
                                label="Online"
                            />
                            
                        </Box>
                    </Box>
                ))}

            </Paper> */}
        </Container>
    );
};